/**
 * VTT cue の識別子（WebVTT identifier）の取り決め:
 * - `@` より前: 1トークン（例: `1`（見出し）、`1-2`（本文）、`4-1@S`）。
 * - `idPathParts` は長さ0または1（タイムラインはフラット1段）。
 * - 見出し: パス部に `-` が含まれないとき（本文は `1-1` のように `-` を含める）。見出し識別子に `@` は付けない。
 * - `@` より後: カンマ区切りのプランコード `B`,`R`,`S`。
 */
type PlanCode = "B" | "R" | "S";

const PLAN_CODE_ORDER: PlanCode[] = ["B", "R", "S"];

const _isHeadingPath = (pathPart: string): boolean => {
  const s = pathPart.trim();
  return s.length > 0 && !s.includes("-");
};

const _isHeadingFromIdPathParts = (idPathParts: readonly string[]): boolean =>
  idPathParts.length === 1 && _isHeadingPath(idPathParts[0]);

const _planTagsFromCommaList = (commaList: string): PlanCode[] => {
  const rawCodes = commaList
    .split(",")
    .map((code) => code.trim().toUpperCase())
    .filter((code): code is PlanCode => code === "B" || code === "R" || code === "S");
  const unique = new Set(rawCodes);
  return PLAN_CODE_ORDER.filter((code) => unique.has(code));
};

interface ParsedCueIdentifier {
  /** `@` より前のパス（1要素） */
  readonly idPathParts: readonly string[];
  readonly planTags: readonly PlanCode[];
  readonly isHeading: boolean;
}

/** 識別子を id パス・プランタグに分解する（単一の解析入口） */
const parseCueIdentifier = (id: string | null): ParsedCueIdentifier => {
  if (!id) {
    return { idPathParts: [], planTags: [], isHeading: false };
  }
  const trimmed = id.trim();
  const at = trimmed.indexOf("@");
  const pathPart = at === -1 ? trimmed : trimmed.slice(0, at).trim();
  const idPathParts = pathPart ? [pathPart.trim()] : [];
  let planTags: PlanCode[] = [];
  if (at !== -1 && at < trimmed.length - 1) {
    const planPart = trimmed.slice(at + 1).trim();
    if (planPart) {
      planTags = _planTagsFromCommaList(planPart);
    }
  }
  return {
    idPathParts,
    planTags,
    isHeading: _isHeadingFromIdPathParts(idPathParts),
  };
};

/** 識別子の `@` 以降からプランタグを取り出す */
const planTagsFromCueId = (id: string | null): PlanCode[] =>
  [...parseCueIdentifier(id).planTags];

export {
  type PlanCode,
  type ParsedCueIdentifier,
  parseCueIdentifier,
  planTagsFromCueId,
  PLAN_CODE_ORDER,
};
